/**
 * Which tenant the session on THIS host belongs to.
 *
 * The refresh cookie and its `linkhr_session` sentinel are deliberately scoped
 * to the shared parent domain (`staging.hr.linkco.jo`) so they survive across
 * tenant subdomains. That is what lets a signed-in user reload without a
 * round-trip — and it is also why, after signing into `acme.…`, opening
 * `linkco.…` sent acme's refresh cookie, minted a perfectly valid ACME access
 * token, and rendered acme's data under linkco's name in the corner. No data
 * crossed a tenant boundary — the server answers the token, and the token was
 * honestly acme's — but the page told the user they were somewhere they were
 * not, which is worse than a login prompt.
 *
 * A shared cookie cannot answer "was this session established HERE". This
 * marker can: it is written host-only (no `Domain` attribute), so a sibling
 * subdomain never sees it. Bootstrap refuses to restore a session whose tenant
 * doesn't match the one recorded for the host being visited.
 *
 * Deliberately mirrors {@link loggedOutMarker}: audience-scoped (admin and
 * mobile share an origin in prod and must not clobber each other), localStorage
 * first, a host-only cookie as the storage-disabled fallback, module memory as
 * the last belt.
 *
 * Fails OPEN only when no transport is writable at all — a private window with
 * storage denied should still reach its own tenant. An earlier version also
 * failed open on a merely EMPTY marker, reasoning that "nothing readable" means
 * "nothing to restore". That reasoning does not survive the shared cookie: the
 * first visit to a sibling tenant subdomain has no marker AND a perfectly good
 * refresh credential, which is precisely the case this guard exists to catch.
 * "Cannot remember" and "has not remembered" are now answered separately.
 *
 * The tenant boundary itself is the server's and holds regardless — the token
 * minted is honestly the other tenant's, and the server answers it correctly.
 * What breaks here is the page's claim about WHICH tenant the user is looking
 * at, which is why a wrong-tenant render is worse than a login prompt.
 */
import { getAudience } from "../refresh/authNetworkConfig.js";

const STORAGE_KEY_PREFIX = "linkhr:auth:tenant:";
const COOKIE_PREFIX = "linkhr_tenant_";

/** Tenant ids are uuids; anything else is not written rather than escaped. */
const SAFE_VALUE = /^[A-Za-z0-9_-]{1,64}$/;

function storageKey(aud?: string): string {
  return STORAGE_KEY_PREFIX + (aud ?? getAudience());
}

function cookieName(aud?: string): string {
  return COOKIE_PREFIX + (aud ?? getAudience());
}

let memoryFallback: string | null = null;

/**
 * Can this host remember anything at all?
 *
 * The distinction is the whole guard: "nothing is recorded" and "nothing CAN be
 * recorded" read identically at the get(), and only the first is evidence that a
 * session was never established here. A host that can remember and simply has
 * not is a host the user has not signed into — so a session offered to it came
 * from the shared parent-domain cookie of a sibling tenant.
 */
function storageUsable(): boolean {
  const viaLocalStorage = tryLocalStorage((ls) => {
    const probe = `${STORAGE_KEY_PREFIX}probe`;
    ls.setItem(probe, "1");
    const ok = ls.getItem(probe) === "1";
    ls.removeItem(probe);
    return ok;
  }, false);
  if (viaLocalStorage) return true;
  if (typeof document === "undefined") return false;
  try {
    const probe = `${COOKIE_PREFIX}probe`;
    document.cookie = `${probe}=1; path=/; max-age=10; SameSite=Strict`;
    const ok = new RegExp(`(?:^|;\\s*)${probe}=1(?:;|$)`).test(document.cookie);
    document.cookie = `${probe}=; path=/; max-age=0`;
    return ok;
  } catch {
    return false;
  }
}

function tryLocalStorage<T>(op: (ls: Storage) => T, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const ls = window.localStorage;
    if (!ls) return fallback;
    return op(ls);
  } catch {
    return fallback;
  }
}

function readCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = new RegExp(`(?:^|;\\s*)${cookieName()}=([A-Za-z0-9_-]+)(?:;|$)`).exec(
    document.cookie,
  );
  return match?.[1] ?? null;
}

/** Host-only on purpose — no `Domain`, so sibling tenant subdomains can't read it. */
function writeCookie(value: string, maxAge: number, aud?: string): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${cookieName(aud)}=${value}; path=/; max-age=${maxAge}; SameSite=Strict`;
  } catch {
    // Blocked — the other transports still apply.
  }
}

export const sessionTenantMarker = {
  set(tenantId: string | null | undefined): void {
    if (typeof tenantId !== "string" || !SAFE_VALUE.test(tenantId)) return;
    memoryFallback = tenantId;
    tryLocalStorage((ls) => ls.setItem(storageKey(), tenantId), undefined);
    writeCookie(tenantId, 31_536_000);
  },

  /**
   * Record the marker for ANOTHER audience on this host — the audience
   * exchange (CC-454) mints the TARGET app's session from the SOURCE app, so
   * the source must leave the marker the target's bootstrap will check, or a
   * perfectly valid exchanged session is refused on arrival. Memory fallback
   * deliberately untouched: it belongs to THIS app's audience.
   */
  setForAudience(aud: string, tenantId: string | null | undefined): void {
    if (typeof tenantId !== "string" || !SAFE_VALUE.test(tenantId)) return;
    tryLocalStorage((ls) => ls.setItem(storageKey(aud), tenantId), undefined);
    writeCookie(tenantId, 31_536_000, aud);
  },

  get(): string | null {
    return (
      tryLocalStorage((ls) => ls.getItem(storageKey()), null) ?? readCookie() ?? memoryFallback
    );
  },

  clear(): void {
    memoryFallback = null;
    tryLocalStorage((ls) => ls.removeItem(storageKey()), undefined);
    // Empty, not a placeholder: a surviving `=0` would read back as a recorded
    // tenant id of "0" and refuse every restore on this host.
    writeCookie("", 0);
  },

  /**
   * Whether a session for `tenantId` may be restored on this host.
   *
   * A recorded value that disagrees is refused — the cross-subdomain case, where
   * the only honest answer is a login screen for the tenant the user asked for.
   *
   * NOTHING recorded is refused too, whenever this host is capable of recording.
   * That is the same case wearing different clothes: the very first visit to a
   * sibling tenant subdomain has no marker yet, so treating "absent" as
   * permissive handed the shared parent-domain cookie exactly the opening this
   * marker exists to close — a Saudi tenant's employees and SAR salaries
   * rendering under a Jordanian tenant's host, with no login prompt in between.
   *
   * Only a host that cannot remember at all still fails open, which is the trade
   * described above: a private window with storage denied should reach its own
   * tenant rather than be locked out to close a mislabel.
   */
  allowsRestore(tenantId: string | null | undefined): boolean {
    const recorded = sessionTenantMarker.get();
    if (recorded === null) return !storageUsable();
    if (typeof tenantId !== "string" || tenantId === "") return true;
    return recorded === tenantId;
  },
};
